// src/components/Assignee.tsx
import { useState, useEffect, useRef, useMemo, memo } from "react";
import {
  Button,
  Popover,
  Text,
  Combobox,
  Avatar,
  Notification,
  lighten,
  Loader,
  useCombobox,
} from "@mantine/core";
import { IconUserPlus, IconChevronDown, IconUser } from "@tabler/icons-react";
import "./Assignee.scss";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../Can";
import { useAssignee } from "../../../utils/hooks/useAssignee";
import SelectOption from "./SelectOption/SelectOption";

interface AssigneeProps {
  assignee: {
    name?: string;
    [key: string]: any;
  } | null;
  clientId: string;
  color: string;
}

export interface Member {
  avatar?: JSX.Element;
  name?: string;
  firstName?: string;
  lastName?: string;
  userId: string;
}

const Assignee = memo(
  ({ assignee: initialAssignee, clientId, color }: AssigneeProps) => {
    const [opened, setOpened] = useState<boolean>(false);
    const [value, setValue] = useState<string | null>(
      initialAssignee?.name ?? null
    );

    const [error, setError] = useState<string | null>(null);
    const [assignee, setAssignee] = useState(initialAssignee);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const ability = useAbility(AbilityContext);
    const canAssignClient = ability.can("assign", "Clients");

    const {
      members: memberList, // This is now the processed members array with 'name' and 'avatar'
      isLoadingMembers,
      isErrorMembers,
      membersError,
      assignClient,
      isAssigning,
      assignError,
    } = useAssignee(clientId);

    // Memoize the avatar component to prevent re-creation on every render
    const defaultAvatar = useMemo(
      () => (
        <Avatar bg="#F16E00">
          <IconUser size={18} color="white" />
        </Avatar>
      ),
      []
    );

    // Memoize the members computation to prevent infinite re-renders
    const activeMemberList = useMemo(() => {
      if (!memberList) return [];

      const activeMembers = memberList.filter(
        (member: any) => member?.status === "active"
      );

      return activeMembers.map((member: any) => ({
        ...member,
        name: `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim(),
        avatar: defaultAvatar,
      }));
    }, [memberList, defaultAvatar]);

    useEffect(() => {
      setAssignee(initialAssignee);
      setValue(initialAssignee?.name ?? null);
    }, [initialAssignee]);

    useEffect(() => {
      if (isErrorMembers) {
        setError(
          `Failed to fetch members. Please try again later. ${
            membersError?.message ?? ""
          }`
        );
      }
    }, [isErrorMembers, membersError]);

    useEffect(() => {
      if (assignError) {
        setError("Failed to assign member. Please try again later.");
      }
    }, [assignError]);

    const handleOptionSubmit = async (val: string) => {
      try {
        const selectedMember = activeMemberList?.find(
          (member) => member.name === val
        );
        let memberId: string | null;

        if (val === "unassigned") {
          memberId = null;
        } else if (selectedMember) {
          memberId = selectedMember.userId;
        } else {
          memberId = "";
        }

        await assignClient(memberId);

        setValue(val === "unassigned" ? null : val);
        setAssignee(
          selectedMember
            ? {
                name: selectedMember.name,
                firstName: selectedMember.firstName,
                lastName: selectedMember.lastName,
              }
            : null
        ); // Update the assignee state
        setOpened(false);
      } catch (error) {
        console.error("Error assigning member:", error);
        // Error is already handled by the hook
      }
    };

    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
      onDropdownOpen: (eventSource) => {
        if (eventSource === "keyboard") {
          combobox.selectActiveOption();
        } else {
          combobox.updateSelectedOptionIndex("active");
        }
      },
    });

    const unassignedAvatar = useMemo(
      () => (
        <Avatar bg={"lightgray"}>
          <IconUser size={18} color="gray" />
        </Avatar>
      ),
      []
    );

    const fallbackAvatar = useMemo(
      () => (
        <Avatar bg="gray">
          <IconUser size={18} color="white" />
        </Avatar>
      ),
      []
    );

    const options = useMemo(
      () => [
        <Combobox.Option value="unassigned" key="unassigned">
          <SelectOption
            avatar={unassignedAvatar}
            userId="null"
            firstName="Unassigned"
            lastName=""
            selected={"unassigned" === value}
          />
        </Combobox.Option>,
        ...(activeMemberList?.map((member: Member) => {
          const memberName = member.firstName + " " + member.lastName;
          return (
            <Combobox.Option value={memberName} key={member.userId}>
              <SelectOption
                avatar={member.avatar ?? fallbackAvatar}
                userId={member.userId}
                firstName={member.firstName ?? ""}
                lastName={member.lastName ?? ""}
                selected={memberName === value}
              />
            </Combobox.Option>
          );
        }) || []),
      ],
      [activeMemberList, value, unassignedAvatar, fallbackAvatar]
    );

    const isEmptyAssignee =
      !assignee ||
      Object.keys(assignee).length === 0 ||
      Object.values(assignee).every((value) => value === null);

    const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation(); // Prevent the row's onClick from being triggered
      if (canAssignClient) {
        setOpened(true);
        combobox.openDropdown();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(".employee-search")
      ) {
        setOpened(false);
        combobox.closeDropdown();
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div ref={wrapperRef} data-assignee onClick={(e) => e.stopPropagation()}>
        {error && (
          <Notification color="red" onClose={() => setError(null)}>
            {error}
          </Notification>
        )}
        <Popover opened={opened} onChange={setOpened}>
          <Popover.Target>
            {isEmptyAssignee ? (
              <Button
                leftSection={
                  <IconUserPlus className="assignee-icon" size="25" />
                }
                variant="transparent"
                onClick={handlePopoverClick}
              >
                <Text className="assignee-text" fw="500">
                  Assign to User
                </Text>
              </Button>
            ) : (
              <Button
                radius="xl"
                rightSection={
                  canAssignClient && (
                    <IconChevronDown className="assignee-icon" size="20" />
                  )
                }
                leftSection={
                  <Avatar bg={color} size={"25px"}>
                    <span style={{ color: "white" }}>
                      {assignee?.firstName?.charAt(0)}
                      {assignee?.lastName?.charAt(0)}
                    </span>
                  </Avatar>
                }
                color={lighten(color, 0.6)}
                onClick={handlePopoverClick}
              >
                <Text
                  data-testid="assignee-name"
                  className="assignee-text"
                  fw="500"
                >
                  {assignee
                    ? `${assignee.firstName} ${assignee.lastName}`
                    : "No name provided"}
                </Text>
              </Button>
            )}
          </Popover.Target>
          <Popover.Dropdown w={"300px"} h={"400px"}>
            {isLoadingMembers || isAssigning ? (
              <div className="member-loader">
                <Text c="dimmed" size="sm">
                  {isLoadingMembers
                    ? "Loading Team Members"
                    : "Assigning Member..."}
                </Text>
                <Loader size="sm" type="bars" />
              </div>
            ) : (
              <Combobox
                store={combobox}
                withinPortal={false}
                onOptionSubmit={(val) => {
                  handleOptionSubmit(val);
                }}
                styles={{ options: { textTransform: "none" } }}
              >
                <Combobox.Dropdown>
                  <Combobox.Options w={"275px"}>{options}</Combobox.Options>
                </Combobox.Dropdown>
              </Combobox>
            )}
          </Popover.Dropdown>
        </Popover>
      </div>
    );
  }
);

Assignee.displayName = "Assignee";

export default Assignee;
